import { useCallback, useEffect, useRef, useState } from "react"
// Handle authentication state so we can hide interactive UI until a user is signed in
import Auth from "@aws-amplify/auth"
import { Hub } from "@aws-amplify/core"
import awsconfig from "awsconfig"

import { AuthorizationType } from "client/types"
import { updateAppVars } from "client/localVars"
import { useOnlineContext } from "context/OnlineContext"
import { UserProvider } from "../context/user"
import MessagePage from "./Page/MessagePage"
import { HostedUICognitoUser } from "context/user/utils"
import { beginHostedUISignIn, clearHostedUIReturnRoute } from "auth/hostedUI"

export const Constants = {
  AUTHENTICATOR_AUTHSTATE: "badger-authenticator-authState"
}

type AuthenticatorProps = {
  onStateChange?: Function
}

interface StateAction {
  currentState?: string
  authData: HostedUICognitoUser | null
  error: string | null
}

const authenticationFailureMessage =
  "Titan could not finish signing you in. Check your connection and try again."

const Authenticator: React.FC<AuthenticatorProps> = ({ onStateChange }) => {
  const { cachePersisted, onlineChecked } = useOnlineContext()

  const [authState, setAuthState] = useState<StateAction>({
    currentState: "loading",
    authData: null,
    error: null
  })
  const currentState = useRef(authState.currentState)

  const handleStateChange = useCallback(
    (newState: string, data: HostedUICognitoUser | null) => {
      if (newState === currentState.current) {
        return
      }

      if (newState === "signedOut") {
        newState = "signIn"
      }

      try {
        localStorage.setItem(Constants.AUTHENTICATOR_AUTHSTATE, newState)
      } catch (e) {
        console.debug("Failed to set the auth state into local storage", e)
      }

      currentState.current = newState

      setAuthState({
        currentState: newState,
        authData: data,
        error: null
        // showToast: false,
      })

      if (onStateChange) {
        onStateChange(newState, data)
      }
    },
    [onStateChange]
  )

  const checkUser = useCallback(() => {
    return Auth.currentAuthenticatedUser()
      .then(user => {
        handleStateChange("signedIn", user as HostedUICognitoUser)
      })
      .catch(() => {
        return beginHostedUISignIn().catch(() => {
          handleStateChange("authError", null)
        })
      })
  }, [handleStateChange])

  const hydrateHostedUIUser = useCallback(() => {
    return Auth.currentAuthenticatedUser()
      .then(user => {
        handleStateChange("signedIn", user as HostedUICognitoUser)
      })
      .catch(() => {
        if (currentState.current !== "signedIn") {
          handleStateChange("authError", null)
        }
      })
  }, [handleStateChange])

  const retryHostedUISignIn = useCallback(() => {
    handleStateChange("signIn", null)
    beginHostedUISignIn().catch(() => {
      handleStateChange("authError", null)
    })
  }, [handleStateChange])

  const authListener = useCallback(
    ({ channel, payload }) => {
      if (channel !== "auth") return
      switch (payload.event) {
        case "cognitoHostedUI":
          // Amplify's hosted-UI event carries the CognitoUser used during the
          // code exchange, but currentAuthenticatedUser hydrates the complete
          // attributes contract expected by UserProvider.
          hydrateHostedUIUser()
          break
        case "cognitoHostedUI_failure":
          if (currentState.current === "signedIn") break
          handleStateChange("authError", null)
          break
        case "parsingUrl_failure":
          if (currentState.current === "signedIn") break
          handleStateChange("authError", null)
          break
        case "signOut":
          clearHostedUIReturnRoute()
          handleStateChange("signIn", null)
          break
        case "customGreetingSignOut":
          clearHostedUIReturnRoute()
          handleStateChange("signIn", null)
          break
        default:
          break
      }
    },
    [handleStateChange, hydrateHostedUIUser]
  )

  useEffect(() => {
    // Listen for auth events
    Hub.listen("auth", authListener)

    // These actions are only performed when mounted
    Auth.configure(awsconfig)

    const isHostedUICallback =
      /^\/callback(?:\/|$)/i.test(window.location.pathname) &&
      (new URLSearchParams(window.location.search).has("code") ||
        new URLSearchParams(window.location.search).has("error"))

    // OAuth code exchange is owned by Amplify's Hub lifecycle. Rechecking the
    // user while that one-time callback is still parsing can start a second
    // Hosted UI redirect. Ordinary protected routes bootstrap exactly once.
    if (!isHostedUICallback) {
      checkUser()
    }

    return () => {
      // Stop listening if we are unmounted
      Hub.remove("auth", authListener)
    }
  }, [authListener, checkUser])

  // Set/unset the authorization method so the apollo link chain knows which
  // headers to add to requests
  useEffect(() => {
    updateAppVars({ authorization: AuthorizationType.Cognito })

    return () => {
      updateAppVars({ authorization: AuthorizationType.None })
    }
  }, [])

  let content: JSX.Element
  // Authentication can proceed over the network without a controlling
  // service worker. Never make controller installation a prerequisite for
  // rendering: Safari can otherwise remain here forever after a failed or
  // delayed service-worker registration.
  if (!cachePersisted || !onlineChecked) {
    content = <MessagePage title="Loading..." />
  } else if (authState.currentState === "signedIn") {
    content = (
      <UserProvider authenticatedUser={authState.authData ?? undefined} />
    )
  } else if (authState.currentState === "authError") {
    content = (
      <MessagePage
        title="Authentication failed"
        subTitle={authenticationFailureMessage}
        actionLabel="Try again"
        onAction={retryHostedUISignIn}
      />
    )
  } else {
    content = <MessagePage title="Authenticating" />
  }

  return content
}

export default Authenticator
